import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface EmptyStateProps {
  title: string;
  description?: string;
  /** Primary + optional secondary actions. */
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

/** Default illustration — a friendly Pulse coral-gradient pulse motif. */
function DefaultArt() {
  return (
    <svg viewBox="0 0 96 96" className="h-20 w-20" fill="none" aria-hidden>
      <defs>
        <linearGradient id="es-coral" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FF5E62" />
          <stop offset="1" stopColor="#FF9966" />
        </linearGradient>
      </defs>
      <circle cx="48" cy="48" r="40" className="fill-coral-soft" />
      <circle cx="48" cy="48" r="40" stroke="url(#es-coral)" strokeWidth="2" strokeDasharray="4 6" strokeOpacity="0.5" />
      <path
        d="M24 50h12l6-18 10 34 7-22 4 6h9"
        stroke="url(#es-coral)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Zero-data placeholder. Centered, encouraging, with a clear next action so
 * empty screens never feel like dead ends.
 */
export function EmptyState({ title, description, action, icon, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-xl bg-surface px-6 py-16 text-center ring-1 ring-line shadow-sm",
        className,
      )}
    >
      <div className="mb-5">{icon ?? <DefaultArt />}</div>
      <h3 className="font-display text-xl font-bold text-body">{title}</h3>
      {description && (
        <p className="mt-2 max-w-sm text-sm text-body-muted">{description}</p>
      )}
      {action && <div className="mt-6 flex flex-wrap items-center justify-center gap-3">{action}</div>}
    </div>
  );
}
